import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Module1() {
  const { user } = useAuth();
  const isManager = ["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(user.role);
  const [analysis, setAnalysis] = useState(null);
  const [users, setUsers] = useState([]);
  const [description, setDescription] = useState("");
  const [hasCustom, setHasCustom] = useState(false);
  const [customText, setCustomText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/analysis").then((r) => {
      setAnalysis(r.data);
      setDescription(r.data.description || "");
      setHasCustom(!!r.data.has_custom_goals);
      setCustomText(r.data.custom_goals_text || "");
    }).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/analysis", { description, has_custom_goals: hasCustom, custom_goals_text: customText });
      toast.success("Analysen har sparats och versionshanterats");
      const { data } = await api.get("/analysis");
      setAnalysis(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!analysis) return <div className="text-sm text-slate-500">Laddar...</div>;
  const signed = users.filter((u) => u.reko_policy_signed).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="modul1-view">
      <div className="border-b border-slate-200 pb-4 mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider">Modul 1 · Reko 200–210</div>
        <h2 className="text-xl font-extrabold text-slate-900">Analys & Kvalitetsmål</h2>
        <p className="text-sm text-slate-500 mt-1">Dokumentera byråns unika förutsättningar och fastställ obligatoriska samt byråspecifika kvalitetsmål.</p>
      </div>

      <div className="space-y-8">
        <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-slate-800 text-sm">1.1 Kartläggning av Verksamheten (p. 1)</h3>
              <p className="text-xs text-slate-500">Analys av byråns storlek, struktur och anpassning av FAR:s grundmallar.</p>
            </div>
            <span className="text-xs bg-blue-100 text-blue-800 font-semibold px-2 py-0.5 rounded">Versionshanterad</span>
          </div>
          <textarea
            data-testid="analysis-description-input"
            className="w-full text-xs p-3 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 font-mono h-28 disabled:opacity-60"
            value={description} onChange={(e) => setDescription(e.target.value)} disabled={!isManager}
            placeholder="Beskriv byråns verksamhet, antal konsulter, kundkategorier och hur FAR-mallar anpassats..."
          />
          <div className="flex justify-between items-center text-xs text-slate-500">
            <span data-testid="analysis-last-saved">
              {analysis.updated_at ? `Senast sparad: ${new Date(analysis.updated_at).toLocaleDateString("sv-SE")}` : "Ej sparad ännu"}
            </span>
            {isManager && (
              <button
                data-testid="analysis-save-btn" onClick={save} disabled={saving}
                className="bg-blue-600 text-white font-semibold px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-60 flex items-center"
              >
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? "Sparar..." : "Spara Ändringar"}
              </button>
            )}
          </div>
        </div>

        <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 space-y-3">
          <h3 className="font-bold text-slate-800 text-sm">1.2 Obligatoriskt Kvalitetsmål (Reko 200 p. 1)</h3>
          <p className="text-xs text-slate-600">I Reko ingår det obligatoriska kvalitetsmålet att utan undantag efterleva Reko, FAR:s yrkesetiska regler samt tillämpliga lagar och regler.</p>
          <div className="p-3 bg-white border border-slate-200 rounded flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">Status för samtliga medarbetares signering:</span>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-200" data-testid="signing-matrix-status">
              {signed} av {users.length} signerade ({users.length ? Math.round((signed / users.length) * 100) : 0}%)
            </span>
          </div>
          <div className="space-y-1.5" data-testid="signing-matrix">
            {users.map((u) => (
              <div key={u.id} className="flex justify-between items-center text-xs bg-white border border-slate-100 rounded px-3 py-2">
                <span className="font-medium text-slate-700">{u.name} <span className="text-slate-400">· {u.role}</span></span>
                {u.reko_policy_signed ? (
                  <span className="text-emerald-600 font-bold">Signerad {u.reko_signed_at ? new Date(u.reko_signed_at).toLocaleDateString("sv-SE") : ""}</span>
                ) : (
                  <span className="text-amber-600 font-bold">Ej signerad</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 space-y-3">
          <h3 className="font-bold text-slate-800 text-sm">1.3 Byråspecifika Kvalitetsmål (p. 1)</h3>
          <p className="text-xs text-slate-600">Har byrån analyserat behovet och valt att fastställa ytterligare byråspecifika kvalitetsmål?</p>
          <div className="flex items-center space-x-6 text-xs font-medium">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input data-testid="custom-goals-yes" type="radio" name="customGoalsRadio" checked={hasCustom} onChange={() => setHasCustom(true)} disabled={!isManager} />
              <span>Ja, ytterligare kvalitetsmål har fastställts</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input data-testid="custom-goals-no" type="radio" name="customGoalsRadio" checked={!hasCustom} onChange={() => setHasCustom(false)} disabled={!isManager} />
              <span>Nej, de obligatoriska målen bedöms tillräckliga</span>
            </label>
          </div>
          {hasCustom && (
            <div className="mt-3 space-y-2">
              <label className="block text-xs font-semibold text-slate-700">Motivering och specifikation av byråmål (obligatoriskt):</label>
              <textarea
                data-testid="custom-goals-text-input"
                className="w-full text-xs p-3 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 h-20 disabled:opacity-60"
                value={customText} onChange={(e) => setCustomText(e.target.value)} disabled={!isManager}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
